export const ITEM_DONATION_UNIT_OPTIONS = [
  { value: "kg", label: "Kilogram (kg)", short: "kg", placeholder: "e.g. 5" },
  { value: "gram", label: "Gram (g)", short: "g", placeholder: "e.g. 500" },
  { value: "litre", label: "Litre (L)", short: "L", placeholder: "e.g. 2" },
  { value: "packet", label: "Packet", short: "packet", placeholder: "e.g. 2" },
  { value: "piece", label: "Piece", short: "piece", placeholder: "e.g. 5" },
  { value: "box", label: "Box", short: "box", placeholder: "e.g. 3" },
  { value: "dozen", label: "Dozen", short: "dozen", placeholder: "e.g. 1" },
  { value: "bundle", label: "Bundle", short: "bundle", placeholder: "e.g. 2" },
  { value: "set", label: "Set", short: "set", placeholder: "e.g. 1" },
] as const;

export type ItemDonationUnit =
  (typeof ITEM_DONATION_UNIT_OPTIONS)[number]["value"];

export function isItemDonationUnit(
  unit?: string | null
): unit is ItemDonationUnit {
  return ITEM_DONATION_UNIT_OPTIONS.some(
    o => o.value === (unit ?? "").toLowerCase()
  );
}

function getUnitOption(unit?: string | null) {
  return ITEM_DONATION_UNIT_OPTIONS.find(
    o => o.value === (unit ?? "").toLowerCase()
  );
}

export function unitAmountLabel(unit?: string | null): string {
  return getUnitOption(unit)?.label ?? "Amount";
}

export function unitAmountPlaceholder(unit?: string | null): string {
  return getUnitOption(unit)?.placeholder ?? "Enter amount";
}

export function formatDonationUnitLabel(unit?: string | null): string | null {
  if (!unit) {
    return null;
  }
  return getUnitOption(unit)?.label ?? unit;
}

export function formatDonationQuantityLabel(
  quantity: number,
  unit?: string | null,
  unitQty?: number | null
): string {
  const option = getUnitOption(unit);
  if (unitQty !== null && unitQty !== undefined && unitQty > 0 && option) {
    return `${quantity} × ${unitQty} ${option.short}`;
  }
  const unitLabel = formatDonationUnitLabel(unit);
  return unitLabel ? `${quantity} ${unitLabel}` : String(quantity);
}

export interface ItemDonationPayload {
  full_name: string;
  mobile_number: string;
  email?: string;
  item_category: number;
  item_name: string;
  quantity: number;
  unit?: string;
  unit_qty?: number;
  preferred_donation_date?: string;
  pickup_address: string;
  area: string;
  landmark?: string;
  pincode: string;
  message?: string;
  status?: string;
}

export interface ScrapDonationPayload {
  full_name: string;
  mobile_number: string;
  email?: string;
  item_category: number;
  item_name: string;
  quantity: number;
  item_condition?: "scrap";
  pickup_address: string;
  area: string;
  landmark?: string;
  pincode: string;
  message?: string;
  photo?: File | null;
  status?: string;
}

export const DONATION_REQUEST_STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "pending", label: "Pending" },
  { value: "contacted", label: "Contacted" },
  { value: "scheduled", label: "Scheduled" },
  { value: "received", label: "Received" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
] as const;

export const DONATION_REQUEST_FILTER_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
] as const;

export type DonationRequestStatus =
  (typeof DONATION_REQUEST_STATUS_OPTIONS)[number]["value"];

const DONATION_REQUEST_STATUS_SET = new Set<string>(
  DONATION_REQUEST_STATUS_OPTIONS.map(o => o.value)
);

export function isDonationRequestStatus(status?: string | null) {
  return DONATION_REQUEST_STATUS_SET.has((status ?? "").toLowerCase());
}

export function formatDonationRequestStatusLabel(
  status?: string | null
): string | null {
  const hit = DONATION_REQUEST_STATUS_OPTIONS.find(
    o => o.value === (status ?? "").toLowerCase()
  );
  return hit?.label ?? null;
}

export function donationRequestStatusClass(status?: string | null): string {
  switch ((status ?? "").toLowerCase()) {
    case "new":
      return "bg-slate-500/15 text-slate-700 hover:bg-slate-500/15";
    case "pending":
      return "bg-blue-500/15 text-blue-700 hover:bg-blue-500/15";
    case "contacted":
      return "bg-amber-500/15 text-amber-700 hover:bg-amber-500/15";
    case "scheduled":
      return "bg-violet-500/15 text-violet-700 hover:bg-violet-500/15";
    case "received":
      return "bg-teal-500/15 text-teal-700 hover:bg-teal-500/15";
    case "approved":
      return "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15";
    case "rejected":
      return "bg-rose-500/15 text-rose-700 hover:bg-rose-500/15";
    case "cancelled":
      return "bg-muted text-muted-foreground hover:bg-muted";
    default:
      return "bg-muted text-muted-foreground hover:bg-muted";
  }
}

export type DonationStatusAction = "approved" | "rejected";

export interface DonationStatusUpdatePayload {
  status: DonationStatusAction;
  remarks: string;
}

export interface PickupUpdatePayload {
  pickup_note: string;
  pickup_date: string;
}

export interface PickupUpdateEmailResult {
  donor_sent: boolean;
  donor_error?: string | null;
}

export interface ItemDonationRecord extends ItemDonationPayload {
  id: number;
  item_category_name?: string;
  remarks?: string | null;
  pickup_note?: string | null;
  pickup_date?: string | null;
  /** True after pickup update email was successfully delivered to the donor. */
  donor_sent?: boolean | null;
  created_at?: string;
  updated_at?: string;
}

export interface ScrapDonationRecord {
  id: number;
  full_name: string;
  mobile_number: string;
  email?: string | null;
  item_category: number;
  item_category_name?: string;
  item_name: string;
  quantity: number;
  item_condition?: string | null;
  pickup_address: string;
  area: string;
  landmark?: string | null;
  pincode: string;
  message?: string | null;
  photo?: string | null;
  status?: string | null;
  remarks?: string | null;
  pickup_note?: string | null;
  pickup_date?: string | null;
  /** True after pickup update email was successfully delivered to the donor. */
  donor_sent?: boolean | null;
  created_at?: string;
  updated_at?: string;
}
